import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import mermaid from "mermaid";
import {
  ArrowLeft, Upload, FileText, Mic, Sparkles, Loader2, Check, Copy,
  ClipboardList, GitBranch, ShieldAlert, Brain, X, AlertTriangle,
  CheckCircle2, XCircle, Download, FileCode2, Video, Zap,
  GraduationCap, Bot, Save, Edit3, Eye, Library, Lock, Crown, TrendingUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const FRAMEWORKS = ["GDPR", "HIPAA", "SOC 2", "ISO 27001"];
const ACCENT = "#7EE787"; // operations-ops green accent
const ACCENT_DEEP = "#43A047";

function authHeaders() {
  try {
    const raw = sessionStorage.getItem("user_data");
    if (!raw) return {};
    const d = JSON.parse(raw);
    return d?.session_token ? { Authorization: `Bearer ${d.session_token}` } : {};
  } catch { return {}; }
}
function isLoggedIn() {
  try { return !!JSON.parse(sessionStorage.getItem("user_data") || "{}").session_token; }
  catch { return false; }
}

mermaid.initialize({
  startOnLoad: false,
  theme: "dark",
  themeVariables: {
    primaryColor: "#1a1a2e",
    primaryTextColor: "#fafafa",
    primaryBorderColor: "#7EE787",
    lineColor: "#A8A8C0",
    secondaryColor: "#27272A",
    tertiaryColor: "#0a0a0a",
  },
});

/* ─── Copy button ─── */
function CopyBtn({ text, label = "Copy", testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 transition-colors"
      data-testid={testid}
    >
      {done ? <><Check size={12} className="text-emerald-400" /> Copied</> : <><Copy size={12} /> {label}</>}
    </button>
  );
}

/* ─── Mermaid renderer ─── */
function MermaidDiagram({ code, testid }) {
  const ref = useRef(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!code || !ref.current) return;
    let cancelled = false;
    (async () => {
      try {
        setErr("");
        const id = "mmd-" + Math.random().toString(36).slice(2, 10);
        const { svg } = await mermaid.render(id, code);
        if (!cancelled && ref.current) ref.current.innerHTML = svg;
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to render diagram.");
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 overflow-x-auto" data-testid={testid}>
      {err && <p className="text-amber-400 text-xs mb-2">{err}</p>}
      <div ref={ref} className="flex justify-center min-h-[200px]" />
    </div>
  );
}

/* ─── Severity pill ─── */
function SevPill({ severity }) {
  const map = {
    Critical: "bg-red-500/15 border-red-500/30 text-red-300",
    High:     "bg-orange-500/15 border-orange-500/30 text-orange-300",
    Medium:   "bg-amber-500/15 border-amber-500/30 text-amber-300",
    Low:      "bg-emerald-500/15 border-emerald-500/30 text-emerald-300",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${map[severity] || map.Low}`}>{severity}</span>;
}

/* ─── PAGE ─── */
export default function SopStudioPage() {
  // Pipeline state
  const [stage, setStage] = useState("input"); // input → sop → outputs
  const [rawNotes, setRawNotes] = useState("");
  const [titleHint, setTitleHint] = useState("");
  const [industry, setIndustry] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parseErr, setParseErr] = useState("");
  const [sourceInfo, setSourceInfo] = useState(null);

  // SOP
  const [generating, setGenerating] = useState(false);
  const [sopErr, setSopErr] = useState("");
  const [sop, setSop] = useState(null);

  // Outputs tab state
  const [activeOutput, setActiveOutput] = useState("sop"); // sop | checklist | flow | risk | training | automation
  const [checklist, setChecklist] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [flow, setFlow] = useState(null);
  const [flowLoading, setFlowLoading] = useState(false);
  const [riskFrameworks, setRiskFrameworks] = useState(["GDPR", "SOC 2"]);
  const [risk, setRisk] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskErr, setRiskErr] = useState("");

  // Phase 2 state
  const [training, setTraining] = useState(null);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingErr, setTrainingErr] = useState("");
  const [trainingAudience, setTrainingAudience] = useState("");
  const [trainingDuration, setTrainingDuration] = useState(60);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [automation, setAutomation] = useState(null);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [automationErr, setAutomationErr] = useState("");

  // Edit / Save state
  const [editingSop, setEditingSop] = useState(false);
  const [editBuffer, setEditBuffer] = useState("");
  const [savedSopId, setSavedSopId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // Phase 3: Pro gate + impact analysis
  const [isPro, setIsPro] = useState(false);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactErr, setImpactErr] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    // Fetch Pro status once on mount (silent — works for logged-out users too)
    axios.get(`${API}/sop-studio/pro-status`, { headers: authHeaders() })
      .then(r => setIsPro(!!r.data.is_pro))
      .catch(() => setIsPro(false));
  }, []);

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setParsing(true); setParseErr(""); setSourceInfo(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/sop-studio/parse-input`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90_000 });
      setRawNotes(res.data.extracted_text);
      setSourceInfo({ filename: res.data.filename, size_mb: res.data.size_mb, words: res.data.word_count, kind: res.data.source_kind });
    } catch (err) {
      setParseErr(err?.response?.data?.detail || err.message || "Could not parse file.");
    } finally { setParsing(false); e.target.value = ""; }
  }

  async function generateSop() {
    if (rawNotes.trim().length < 20) { setSopErr("Add at least 20 characters of source notes."); return; }
    setGenerating(true); setSopErr(""); setSop(null);
    try {
      const res = await axios.post(`${API}/sop-studio/generate-sop`, {
        raw_notes: rawNotes,
        title_hint: titleHint || null,
        industry: industry || null,
      }, { timeout: 90_000 });
      setSop(res.data);
      setStage("outputs");
      setActiveOutput("sop");
      // Reset derivatives when SOP changes
      setChecklist(null); setFlow(null); setRisk(null);
      setTraining(null); setAutomation(null);
      setSavedSopId(null); setSaveMsg(""); setEditingSop(false);
    } catch (err) {
      setSopErr(err?.response?.data?.detail || err.message || "SOP generation failed.");
    } finally { setGenerating(false); }
  }

  async function loadChecklist() {
    if (checklist || checklistLoading) return;
    setChecklistLoading(true);
    try {
      const res = await axios.post(`${API}/sop-studio/checklist`, { sop_markdown: sop.sop_markdown }, { timeout: 90_000 });
      setChecklist(res.data);
    } catch (err) { console.error(err); } finally { setChecklistLoading(false); }
  }
  async function loadFlow() {
    if (flow || flowLoading) return;
    setFlowLoading(true);
    try {
      const res = await axios.post(`${API}/sop-studio/process-flow`, { sop_markdown: sop.sop_markdown }, { timeout: 90_000 });
      setFlow(res.data);
    } catch (err) { console.error(err); } finally { setFlowLoading(false); }
  }
  async function loadRisk() {
    setRiskLoading(true); setRiskErr("");
    try {
      const res = await axios.post(`${API}/sop-studio/risk-assessment`, {
        sop_markdown: sop.sop_markdown, frameworks: riskFrameworks, industry: industry || null,
      }, { timeout: 90_000 });
      setRisk(res.data);
    } catch (err) { setRiskErr(err?.response?.data?.detail || "Risk analysis failed."); } finally { setRiskLoading(false); }
  }

  async function loadTraining() {
    if (training || trainingLoading) return;
    setTrainingLoading(true); setTrainingErr("");
    try {
      const res = await axios.post(`${API}/sop-studio/training-module`, {
        sop_markdown: sop.sop_markdown,
        audience: trainingAudience || null,
        duration_minutes: trainingDuration || 60,
      }, { timeout: 90_000 });
      setTraining(res.data);
    } catch (err) {
      setTrainingErr(err?.response?.data?.detail || "Training module generation failed.");
    } finally { setTrainingLoading(false); }
  }

  async function regenerateTraining() {
    setTraining(null);
    await loadTraining();
  }

  async function downloadTrainingPdf() {
    if (!training) return;
    setPdfLoading(true);
    try {
      const res = await axios.post(`${API}/sop-studio/training-module/pdf`, training, {
        responseType: "blob", timeout: 60_000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (training.title || sop?.title || "training").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 60);
      a.download = `${safe}-training.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { setTrainingErr("PDF download failed."); }
    finally { setPdfLoading(false); }
  }

  async function loadAutomation() {
    if (automation || automationLoading) return;
    setAutomationLoading(true); setAutomationErr("");
    try {
      const res = await axios.post(`${API}/sop-studio/automation-ideas`, {
        sop_markdown: sop.sop_markdown,
      }, { timeout: 90_000 });
      setAutomation(res.data);
    } catch (err) {
      setAutomationErr(err?.response?.data?.detail || "Automation analysis failed.");
    } finally { setAutomationLoading(false); }
  }

  function startEditSop() {
    setEditBuffer(sop.sop_markdown);
    setEditingSop(true);
  }
  function cancelEditSop() {
    setEditingSop(false); setEditBuffer("");
  }
  function saveEditInline() {
    setSop(prev => ({ ...prev, sop_markdown: editBuffer, word_count: editBuffer.split(/\s+/).filter(Boolean).length }));
    // Invalidate derivatives
    setChecklist(null); setFlow(null); setRisk(null); setTraining(null); setAutomation(null);
    setEditingSop(false);
    setSaveMsg("SOP updated locally. Remember to Save to Library.");
    setTimeout(() => setSaveMsg(""), 4000);
  }

  async function saveToLibrary() {
    if (!isLoggedIn()) {
      setSaveMsg("Login required to save SOPs.");
      setTimeout(() => setSaveMsg(""), 5000);
      return;
    }
    setSaving(true); setSaveMsg("");
    try {
      const res = await axios.post(`${API}/sop-studio/library/save`, {
        sop_id: savedSopId || null,
        title: sop?.title || titleHint || "Untitled SOP",
        sop_markdown: sop.sop_markdown,
        industry: industry || null,
        title_hint: titleHint || null,
      }, { headers: authHeaders() });
      setSavedSopId(res.data.sop_id);
      setSaveMsg(res.data.status === "created" ? "Saved to library ✓" : `Updated (v${res.data.version}) ✓`);
      setTimeout(() => setSaveMsg(""), 4000);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        setShowUpgrade(true);
        setSaveMsg("");
      } else {
        setSaveMsg(status === 401 ? "Login required to save SOPs." : (detail || "Save failed."));
        setTimeout(() => setSaveMsg(""), 5000);
      }
    } finally { setSaving(false); }
  }

  async function analyzeImpact() {
    if (!isLoggedIn()) {
      setImpactErr("Login required.");
      return;
    }
    if (!isPro) {
      setShowUpgrade(true);
      return;
    }
    setImpactLoading(true); setImpactErr(""); setImpact(null);
    try {
      const res = await axios.post(`${API}/sop-studio/impact-analysis`, {
        sop_id: savedSopId || null,
        baseline_markdown: savedSopId ? null : sop.sop_markdown,
        proposed_markdown: editBuffer,
      }, { headers: authHeaders() });
      setImpact(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) { setShowUpgrade(true); }
      else { setImpactErr(err?.response?.data?.detail || "Impact analysis failed."); }
    } finally { setImpactLoading(false); }
  }

  useEffect(() => {
    if (activeOutput === "checklist") loadChecklist();
    if (activeOutput === "flow") loadFlow();
    if (activeOutput === "risk" && !risk) loadRisk();
    if (activeOutput === "training") loadTraining();
    if (activeOutput === "automation") loadAutomation();
    // eslint-disable-next-line
  }, [activeOutput]);

  function toggleFramework(fw) {
    setRiskFrameworks(prev => prev.includes(fw) ? prev.filter(x => x !== fw) : [...prev, fw]);
  }

  function exportChecklistCsv() {
    if (!checklist) return;
    const rows = [["Phase", "Step", "Owner", "Critical", "Sub-steps"]];
    for (const p of checklist.phases || []) {
      for (const it of p.items || []) {
        rows.push([p.phase, it.text, it.owner || "Any", it.critical ? "YES" : "no",
                   (it.sub_items || []).map(s => s.text).join(" | ")]);
      }
    }
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "sop-checklist.csv"; a.click();
  }

  function resetAll() {
    setStage("input"); setSop(null); setChecklist(null); setFlow(null); setRisk(null);
    setTraining(null); setAutomation(null);
    setSavedSopId(null); setSaveMsg(""); setEditingSop(false); setEditBuffer("");
    setRawNotes(""); setTitleHint(""); setIndustry(""); setSourceInfo(null);
    setActiveOutput("sop");
  }

  const TABS = [
    { id: "sop",        label: "SOP Document",        icon: FileText },
    { id: "checklist",  label: "Execution Checklist", icon: ClipboardList },
    { id: "flow",       label: "Process Flow",        icon: GitBranch },
    { id: "risk",       label: "Risk & Compliance",   icon: ShieldAlert },
    { id: "training",   label: "Training Module",     icon: GraduationCap },
    { id: "automation", label: "Automation Ideas",    icon: Bot },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-zinc-800/60 bg-zinc-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/ai-tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm" data-testid="back-to-tools">
            <ArrowLeft size={16} /> AI Tools
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/ai-tools/sop-studio/library"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-xs font-medium border border-zinc-800"
                  data-testid="library-link">
              <Library size={13} /> My SOP Library
            </Link>
            <div className="flex items-center gap-2" style={{ color: ACCENT }}>
              <Brain size={18} />
              <span className="font-semibold tracking-tight text-sm">ShansoSOP Studio</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium mb-4 border"
               style={{ background: `${ACCENT}1a`, borderColor: `${ACCENT}4d`, color: ACCENT }}>
            <Zap size={11} /> Phase 1 · Audio + video input live · Powered by Claude Sonnet 4.5
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            From a messy recording<br />
            <span className="bg-gradient-to-r from-[#7EE787] to-[#43A047] bg-clip-text text-transparent">to a compliance-ready SOP.</span>
          </h1>
          <p className="text-zinc-400 text-base mt-4 max-w-2xl">
            Drop in notes, PDFs, Word docs, or 30-minute recordings. Get a structured SOP plus a role-tagged checklist,
            Mermaid process flow, and a GDPR/HIPAA/SOC 2/ISO 27001 risk assessment — all in one workflow.
          </p>
        </motion.div>
      </section>

      <main className="max-w-6xl mx-auto px-4 pb-20">
        {stage === "input" ? (
          /* ═════════ INPUT STAGE ═════════ */
          <section className="space-y-6" data-testid="input-stage">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Upload */}
              <div className="bg-zinc-900/40 rounded-2xl p-6 border border-zinc-800/60">
                <h3 className="text-white text-sm font-semibold mb-1 flex items-center gap-2">
                  <Upload size={14} style={{ color: ACCENT }} /> Upload file
                </h3>
                <p className="text-zinc-500 text-xs mb-4">PDF · DOCX · TXT · MD (≤15 MB) · MP3 · MP4 · WAV · M4A (≤25 MB, Whisper limit)</p>
                <label className="block border-2 border-dashed border-zinc-700 hover:border-[#7EE787]/60 rounded-xl p-6 cursor-pointer text-center transition-colors" data-testid="upload-dropzone">
                  <input type="file" className="hidden" onChange={onFile}
                         accept=".pdf,.docx,.txt,.md,.mp3,.mp4,.m4a,.wav,.webm,.mpeg,.mpga"
                         data-testid="input-file" />
                  {parsing ? (
                    <div className="flex items-center justify-center gap-2 text-zinc-400 text-sm">
                      <Loader2 size={16} className="animate-spin" /> Parsing...
                    </div>
                  ) : sourceInfo ? (
                    <div className="space-y-1">
                      <div className="flex items-center justify-center gap-2">
                        {sourceInfo.kind === "audio_video" ? <Video size={14} style={{ color: ACCENT }} /> : <FileText size={14} style={{ color: ACCENT }} />}
                        <span className="text-white text-sm font-medium">{sourceInfo.filename}</span>
                      </div>
                      <p className="text-zinc-500 text-xs">{sourceInfo.size_mb} MB · {sourceInfo.words} words extracted · click to replace</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <p className="text-zinc-300 text-sm">Click to upload or drop a file here</p>
                      <p className="text-zinc-500 text-xs">Audio/video will be transcribed via Whisper</p>
                    </div>
                  )}
                </label>
                {parseErr && <p className="text-amber-400 text-xs mt-3" data-testid="parse-error">{parseErr}</p>}
              </div>

              {/* Context */}
              <div className="bg-zinc-900/40 rounded-2xl p-6 border border-zinc-800/60">
                <h3 className="text-white text-sm font-semibold mb-1 flex items-center gap-2">
                  <Sparkles size={14} style={{ color: ACCENT }} /> Optional context
                </h3>
                <p className="text-zinc-500 text-xs mb-4">Improves SOP quality</p>
                <input type="text" value={titleHint} onChange={(e) => setTitleHint(e.target.value)}
                       placeholder="SOP title hint (e.g. Customer onboarding KYC)"
                       className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#7EE787]/50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-zinc-600 outline-none mb-3"
                       data-testid="input-title-hint" />
                <input type="text" value={industry} onChange={(e) => setIndustry(e.target.value)}
                       placeholder="Industry (e.g. Fintech · Healthcare · E-commerce)"
                       className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#7EE787]/50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-zinc-600 outline-none"
                       data-testid="input-industry" />
              </div>
            </div>

            {/* Raw notes editor */}
            <div className="bg-zinc-900/40 rounded-2xl p-6 border border-zinc-800/60">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white text-sm font-semibold flex items-center gap-2">
                  <Mic size={14} style={{ color: ACCENT }} /> Raw notes / transcript
                </h3>
                <span className="text-zinc-500 text-[11px]">{rawNotes.length} chars · {rawNotes.trim().split(/\s+/).filter(Boolean).length} words</span>
              </div>
              <textarea value={rawNotes} onChange={(e) => setRawNotes(e.target.value)} rows={10}
                        placeholder="Type or paste your notes here. Or upload a file above to auto-fill. Minimum 20 characters."
                        className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#7EE787]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none font-mono leading-relaxed"
                        data-testid="input-raw-notes" />
              {sopErr && <p className="text-amber-400 text-xs mt-3" data-testid="sop-error">{sopErr}</p>}
              <div className="flex justify-end mt-4">
                <button onClick={generateSop} disabled={generating || rawNotes.trim().length < 20}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-black font-bold text-sm disabled:opacity-50"
                        style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                        data-testid="generate-sop-btn">
                  {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {generating ? "Generating SOP..." : "Generate SOP"}
                </button>
              </div>
            </div>
          </section>
        ) : (
          /* ═════════ OUTPUTS STAGE ═════════ */
          <section className="space-y-5" data-testid="outputs-stage">
            {/* Title + reset */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-zinc-500 text-[11px] uppercase tracking-wider">Generated SOP</p>
                <h2 className="text-white text-xl font-bold" data-testid="sop-title">{sop?.title}</h2>
                <p className="text-zinc-500 text-xs mt-0.5">{sop?.word_count} words · Generated {new Date(sop?.generated_at).toLocaleTimeString()}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {saveMsg && <span className="text-[11px] text-[#7EE787] bg-[#7EE787]/10 border border-[#7EE787]/30 px-2 py-1 rounded-md" data-testid="save-message">{saveMsg}</span>}
                <button onClick={saveToLibrary} disabled={saving}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-black font-semibold text-xs disabled:opacity-50"
                        style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                        data-testid="save-to-library-btn"
                        title={isLoggedIn() ? (savedSopId ? "Update saved SOP" : "Save SOP to your library") : "Login required"}>
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  {savedSopId ? "Update" : "Save"}
                </button>
                <button onClick={resetAll}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-xs font-medium border border-zinc-800"
                        data-testid="reset-btn">
                  <X size={13} /> Start over
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex overflow-x-auto -mb-px border-b border-zinc-900">
              {TABS.map(t => {
                const Icon = t.icon;
                const isActive = activeOutput === t.id;
                return (
                  <button key={t.id} onClick={() => setActiveOutput(t.id)}
                          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                            isActive ? "border-[#7EE787]" : "text-zinc-500 border-transparent hover:text-zinc-200"
                          }`}
                          style={isActive ? { color: ACCENT } : undefined}
                          data-testid={`tab-${t.id}`}>
                    <Icon size={15} /> {t.label}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            {activeOutput === "sop" && (
              <div className="space-y-4" data-testid="output-sop">
                <div className="flex justify-end gap-2 flex-wrap">
                  {!editingSop ? (
                    <>
                      <button onClick={startEditSop}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 transition-colors"
                              data-testid="edit-sop-btn">
                        <Edit3 size={12} /> Edit
                      </button>
                      <CopyBtn text={sop.sop_markdown} label="Copy Markdown" testid="sop-copy" />
                    </>
                  ) : (
                    <>
                      {savedSopId && (
                        <button onClick={analyzeImpact} disabled={impactLoading || editBuffer === sop.sop_markdown}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] border transition-colors disabled:opacity-50 ${isPro ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border-zinc-700" : "bg-zinc-900 text-zinc-500 border-zinc-800"}`}
                                data-testid="analyze-impact-btn"
                                title={isPro ? "Analyze impact of pending changes" : "Pro feature — click to upgrade"}>
                          {impactLoading ? <Loader2 size={12} className="animate-spin" /> : isPro ? <TrendingUp size={12} /> : <Lock size={12} />}
                          Analyze impact
                        </button>
                      )}
                      <button onClick={cancelEditSop}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700"
                              data-testid="cancel-edit-btn">
                        <X size={12} /> Cancel
                      </button>
                      <button onClick={saveEditInline}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] text-black font-semibold"
                              style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                              data-testid="confirm-edit-btn">
                        <Check size={12} /> Apply changes
                      </button>
                    </>
                  )}
                </div>
                {editingSop ? (
                  <>
                    {impactErr && <p className="text-amber-400 text-xs" data-testid="impact-error">{impactErr}</p>}
                    {impact && (
                      <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-[#7EE787]/30 rounded-2xl p-5 space-y-3" data-testid="impact-panel">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-[#7EE787] font-bold flex items-center gap-1.5"><TrendingUp size={11} /> Impact analysis</p>
                            <p className="text-white text-sm font-semibold mt-1" data-testid="impact-summary">{impact.change_summary}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider font-bold ${
                              impact.overall_impact === "Critical" ? "bg-red-500/15 border-red-500/40 text-red-300" :
                              impact.overall_impact === "High" ? "bg-orange-500/15 border-orange-500/40 text-orange-300" :
                              impact.overall_impact === "Medium" ? "bg-amber-500/15 border-amber-500/40 text-amber-300" :
                              "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                            }`} data-testid="impact-overall">{impact.overall_impact} impact</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider font-bold ${
                              impact.risk_delta === "Increased" ? "bg-red-500/15 border-red-500/40 text-red-300" :
                              impact.risk_delta === "Reduced" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" :
                              "bg-zinc-800 border-zinc-700 text-zinc-300"
                            }`} data-testid="impact-risk-delta">Risk {impact.risk_delta}</span>
                            <button onClick={() => setImpact(null)} className="text-zinc-500 hover:text-white p-1" data-testid="dismiss-impact"><X size={13} /></button>
                          </div>
                        </div>
                        <p className="text-zinc-400 text-xs">{impact.risk_delta_explanation}</p>
                        <div className="grid sm:grid-cols-3 gap-3 text-xs">
                          {impact.additions?.length > 0 && (
                            <div>
                              <p className="text-emerald-300 font-bold uppercase tracking-wider text-[10px] mb-1">+ Added ({impact.additions.length})</p>
                              <ul className="space-y-0.5 text-zinc-300 list-disc list-inside">
                                {impact.additions.slice(0, 5).map((a, i) => <li key={i}>{a}</li>)}
                              </ul>
                            </div>
                          )}
                          {impact.deletions?.length > 0 && (
                            <div>
                              <p className="text-red-300 font-bold uppercase tracking-wider text-[10px] mb-1">− Removed ({impact.deletions.length})</p>
                              <ul className="space-y-0.5 text-zinc-300 list-disc list-inside">
                                {impact.deletions.slice(0, 5).map((d, i) => <li key={i}>{d}</li>)}
                              </ul>
                            </div>
                          )}
                          {impact.modifications?.length > 0 && (
                            <div>
                              <p className="text-amber-300 font-bold uppercase tracking-wider text-[10px] mb-1">± Modified ({impact.modifications.length})</p>
                              <ul className="space-y-0.5 text-zinc-300 list-disc list-inside">
                                {impact.modifications.slice(0, 5).map((m, i) => <li key={i}>{m}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                        {(impact.training_retrain_needed || impact.automation_reeval_needed || impact.compliance_drift?.length > 0) && (
                          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5 space-y-1 text-xs">
                            {impact.training_retrain_needed && (
                              <p className="text-amber-300"><span className="font-bold">Re-training needed:</span> <span className="text-zinc-300">{(impact.training_topics_to_retrain || []).slice(0, 4).join(" · ") || "Yes"}</span></p>
                            )}
                            {impact.automation_reeval_needed && (
                              <p className="text-amber-300"><span className="font-bold">Re-evaluate automation ideas</span> — current ROI suggestions may no longer apply.</p>
                            )}
                            {impact.compliance_drift?.length > 0 && impact.compliance_drift.map((c, i) => (
                              <p key={i} className="text-amber-300"><span className="font-bold">{c.framework} drift:</span> <span className="text-zinc-300">{c.concern}</span></p>
                            ))}
                          </div>
                        )}
                        {impact.recommendation && (
                          <p className="text-[#7EE787] text-xs italic"><span className="text-zinc-500">Recommendation:</span> {impact.recommendation}</p>
                        )}
                      </div>
                    )}
                  <div className="grid lg:grid-cols-2 gap-4" data-testid="sop-editor">
                    <div>
                      <p className="text-zinc-500 text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1.5"><Edit3 size={11} /> Markdown source</p>
                      <textarea value={editBuffer} onChange={(e) => setEditBuffer(e.target.value)}
                                rows={24}
                                className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#7EE787]/50 rounded-lg px-4 py-3 text-xs text-white placeholder-zinc-600 outline-none font-mono leading-relaxed"
                                data-testid="sop-edit-textarea" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1.5"><Eye size={11} /> Live preview</p>
                      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 prose prose-invert prose-h1:text-2xl prose-h1:font-bold prose-h2:text-lg prose-h2:font-bold prose-h2:text-[#7EE787] prose-h2:mt-5 prose-headings:text-white prose-p:text-zinc-300 prose-li:text-zinc-300 prose-strong:text-white max-w-none max-h-[540px] overflow-y-auto">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{editBuffer}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                  </>
                ) : (
                  <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-6 prose prose-invert prose-h1:text-3xl prose-h1:font-bold prose-h1:mb-4 prose-h1:pb-3 prose-h1:border-b prose-h1:border-zinc-800 prose-h2:text-xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-3 prose-h2:text-[#7EE787] prose-h3:text-base prose-h3:font-semibold prose-h3:mt-4 prose-headings:text-white prose-p:text-zinc-300 prose-p:leading-relaxed prose-li:text-zinc-300 prose-li:my-1 prose-strong:text-white prose-ol:my-3 prose-ul:my-3 max-w-none prose-code:text-emerald-300 prose-blockquote:border-[#7EE787]/40 prose-blockquote:text-amber-300 prose-blockquote:not-italic">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{sop.sop_markdown}</ReactMarkdown>
                  </div>
                )}
              </div>
            )}

            {activeOutput === "checklist" && (
              <div className="space-y-4" data-testid="output-checklist">
                {checklistLoading && <p className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Generating checklist…</p>}
                {checklist && (
                  <>
                    <div className="flex justify-end">
                      <button onClick={exportChecklistCsv}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 text-xs font-medium border border-zinc-800"
                              data-testid="checklist-csv-btn">
                        <Download size={13} /> Export as CSV
                      </button>
                    </div>
                    {checklist.phases?.map((p, i) => (
                      <div key={i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-5">
                        <h4 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider font-bold" style={{ background: `${ACCENT}1a`, color: ACCENT }}>
                            {p.phase}
                          </span>
                          <span className="text-zinc-500 text-[11px]">{p.items?.length || 0} items</span>
                        </h4>
                        <ul className="space-y-2.5">
                          {p.items?.map((it, j) => (
                            <li key={j} className="flex items-start gap-3" data-testid={`item-${it.id}`}>
                              <input type="checkbox" className="mt-1 accent-[#7EE787]" />
                              <div className="flex-1">
                                <div className="flex items-start gap-2 flex-wrap">
                                  <span className="text-white text-sm">{it.text}</span>
                                  {it.critical && <span className="text-[9px] font-bold text-red-300 bg-red-500/15 border border-red-500/30 px-1.5 py-0.5 rounded uppercase tracking-wider">Critical</span>}
                                  <span className="text-zinc-600 text-[10px]">· {it.owner}</span>
                                </div>
                                {it.sub_items?.length > 0 && (
                                  <ul className="mt-1.5 ml-0 space-y-1">
                                    {it.sub_items.map((s, k) => (
                                      <li key={k} className="text-zinc-400 text-xs flex gap-2">
                                        <span className="text-zinc-600">↳</span> {s.text} <span className="text-zinc-600">· {s.owner}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {activeOutput === "flow" && (
              <div className="space-y-4" data-testid="output-flow">
                {flowLoading && <p className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Generating diagram…</p>}
                {flow && (
                  <>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-zinc-400 text-xs">{flow.node_count} nodes · {flow.decision_points?.length || 0} decision points</p>
                      <CopyBtn text={flow.mermaid} label="Copy Mermaid source" testid="flow-copy" />
                    </div>
                    <MermaidDiagram code={flow.mermaid} testid="flow-diagram" />
                    {flow.notes && (
                      <div className="bg-[#7EE787]/5 border border-[#7EE787]/20 rounded-xl p-3">
                        <p className="text-[#7EE787] text-[11px] uppercase tracking-wider font-bold mb-1">Notes</p>
                        <p className="text-zinc-300 text-xs leading-relaxed">{flow.notes}</p>
                      </div>
                    )}
                    <details className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-3">
                      <summary className="cursor-pointer text-zinc-300 text-xs flex items-center gap-2 font-medium"><FileCode2 size={12} /> View Mermaid source</summary>
                      <pre className="mt-3 bg-zinc-950 rounded-lg p-3 text-[11px] text-emerald-300 font-mono overflow-x-auto">{flow.mermaid}</pre>
                    </details>
                  </>
                )}
              </div>
            )}

            {activeOutput === "risk" && (
              <div className="space-y-4" data-testid="output-risk">
                {/* Framework picker */}
                <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-zinc-300 text-sm font-medium">Compliance frameworks to audit against</p>
                      <p className="text-zinc-500 text-[11px] mt-0.5">Select the frameworks relevant to your operation</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {FRAMEWORKS.map(fw => {
                        const on = riskFrameworks.includes(fw);
                        return (
                          <button key={fw} onClick={() => toggleFramework(fw)}
                                  className={`text-[11px] px-3 py-1.5 rounded-full border transition-colors ${on ? "border-[#7EE787]/60 text-[#7EE787] bg-[#7EE787]/10" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"}`}
                                  data-testid={`framework-${fw.replace(/\s/g,'').toLowerCase()}`}>
                            {fw}
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={loadRisk} disabled={riskLoading}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-black font-bold text-xs disabled:opacity-50"
                            style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                            data-testid="run-risk-btn">
                      {riskLoading ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />}
                      {risk ? "Re-run analysis" : "Run analysis"}
                    </button>
                  </div>
                </div>

                {riskLoading && <p className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Running risk analysis…</p>}
                {riskErr && <p className="text-amber-400 text-xs">{riskErr}</p>}

                {risk && (
                  <>
                    <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800/60 rounded-2xl p-6">
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="text-center">
                          <p className="text-7xl font-bold" style={{
                            color: risk.overall_risk_score >= 80 ? "#7EE787" : risk.overall_risk_score >= 60 ? "#FFC857" : "#FF6B6B"
                          }}>{risk.overall_risk_score}</p>
                          <p className="text-zinc-500 text-[11px] uppercase tracking-wider mt-1">Risk Score · {risk.overall_band}</p>
                        </div>
                        <p className="text-zinc-300 text-sm leading-relaxed flex-1 min-w-[240px]">{risk.summary}</p>
                      </div>
                    </div>

                    {/* Risk rows */}
                    <div className="space-y-2">
                      {risk.risks?.map((r, i) => (
                        <div key={i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-3 space-y-1.5" data-testid={`risk-${r.id}`}>
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <p className="text-white text-sm font-medium">{r.area}</p>
                            <div className="flex items-center gap-1.5">
                              <SevPill severity={r.severity} />
                              <span className="text-zinc-500 text-[10px]">×</span>
                              <span className="text-zinc-400 text-[11px]">{r.likelihood}</span>
                              <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-200">{r.score}</span>
                              {r.missing_step && <AlertTriangle size={12} className="text-amber-400" />}
                            </div>
                          </div>
                          <p className="text-zinc-400 text-xs leading-relaxed">{r.description}</p>
                          <p className="text-[#7EE787] text-xs leading-relaxed"><span className="text-zinc-500">Fix:</span> {r.mitigation}</p>
                          {r.frameworks?.length > 0 && (
                            <div className="flex gap-1 flex-wrap">
                              {r.frameworks.map((fw, k) => (
                                <span key={k} className="text-[9px] px-1.5 py-0.5 bg-[#7EE787]/10 text-[#7EE787] border border-[#7EE787]/30 rounded uppercase tracking-wider">{fw}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {risk.compliance_gaps?.length > 0 && (
                      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4" data-testid="compliance-gaps">
                        <p className="text-zinc-300 text-sm font-semibold mb-3">Compliance gaps</p>
                        <div className="space-y-2">
                          {risk.compliance_gaps.map((g, i) => (
                            <div key={i} className="border-l-2 border-red-500/40 pl-3 py-1">
                              <p className="text-white text-xs font-medium"><span className="text-red-300">{g.framework}</span> · {g.requirement}</p>
                              <p className="text-zinc-400 text-xs mt-0.5">Gap: {g.gap}</p>
                              <p className="text-emerald-300 text-xs mt-0.5">Fix: {g.recommendation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {risk.missing_steps?.length > 0 && (
                      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4" data-testid="missing-steps">
                        <p className="text-amber-300 text-sm font-semibold mb-2 flex items-center gap-2"><XCircle size={14} /> Missing steps detected</p>
                        <ul className="space-y-1 text-zinc-300 text-xs list-disc list-inside">
                          {risk.missing_steps.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeOutput === "training" && (
              <div className="space-y-4" data-testid="output-training">
                {/* Controls */}
                <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
                  <div className="grid sm:grid-cols-3 gap-3 items-end">
                    <div>
                      <label className="text-zinc-400 text-[11px] uppercase tracking-wider block mb-1.5">Audience</label>
                      <input type="text" value={trainingAudience} onChange={(e) => setTrainingAudience(e.target.value)}
                             placeholder="e.g. New compliance analysts"
                             className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#7EE787]/50 rounded-lg px-3 py-2 text-xs text-white outline-none"
                             data-testid="training-audience" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-[11px] uppercase tracking-wider block mb-1.5">Duration (min)</label>
                      <input type="number" min={15} max={240} step={15} value={trainingDuration} onChange={(e) => setTrainingDuration(parseInt(e.target.value) || 60)}
                             className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#7EE787]/50 rounded-lg px-3 py-2 text-xs text-white outline-none"
                             data-testid="training-duration" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={regenerateTraining} disabled={trainingLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-black font-bold text-xs disabled:opacity-50 flex-1 justify-center"
                              style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                              data-testid="regenerate-training-btn">
                        {trainingLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                        {training ? "Regenerate" : "Generate"}
                      </button>
                      {training && (
                        <button onClick={downloadTrainingPdf} disabled={pdfLoading}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 text-xs font-medium border border-zinc-800 disabled:opacity-50"
                                data-testid="download-pdf-btn">
                          {pdfLoading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {trainingLoading && <p className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Building training module…</p>}
                {trainingErr && <p className="text-amber-400 text-xs" data-testid="training-error">{trainingErr}</p>}

                {training && (
                  <>
                    <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800/60 rounded-2xl p-5">
                      <p className="text-[11px] uppercase tracking-wider text-[#7EE787] font-semibold mb-1">Training module</p>
                      <h3 className="text-white text-lg font-bold" data-testid="training-title">{training.title}</h3>
                      <p className="text-zinc-400 text-xs mt-1">Audience: {training.audience} · {training.total_duration_min} min total</p>
                    </div>

                    {training.learning_objectives?.length > 0 && (
                      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-5" data-testid="training-objectives">
                        <p className="text-white text-sm font-semibold mb-2">Learning objectives</p>
                        <ul className="space-y-1.5 text-zinc-300 text-xs">
                          {training.learning_objectives.map((o, i) => (
                            <li key={i} className="flex gap-2"><CheckCircle2 size={13} className="text-[#7EE787] mt-0.5 shrink-0" /> {o}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {training.lessons?.length > 0 && (
                      <div className="space-y-2" data-testid="training-lessons">
                        <p className="text-white text-sm font-semibold">Lesson plan</p>
                        {training.lessons.map((L, i) => (
                          <div key={i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-4 space-y-1.5">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <p className="text-white text-sm font-semibold">{i + 1}. {L.title}</p>
                              <div className="flex items-center gap-2">
                                <span className="text-zinc-400 text-[11px]">{L.duration_min} min</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#7EE787]/10 text-[#7EE787] border border-[#7EE787]/30 uppercase tracking-wider">{L.teaching_method}</span>
                              </div>
                            </div>
                            <ul className="text-zinc-300 text-xs space-y-1 list-disc list-inside ml-1">
                              {(L.key_points || []).map((kp, k) => <li key={k}>{kp}</li>)}
                            </ul>
                            {L.trainer_notes && <p className="text-zinc-500 text-[11px] italic">Trainer: {L.trainer_notes}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {(training.pre_test?.length > 0 || training.post_test?.length > 0) && (
                      <div className="grid md:grid-cols-2 gap-3" data-testid="training-tests">
                        {[{ label: "Pre-test", arr: training.pre_test, tid: "pre-test" }, { label: "Post-test", arr: training.post_test, tid: "post-test" }].map(({ label, arr, tid }) => (
                          <div key={label} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4" data-testid={tid}>
                            <p className="text-white text-sm font-semibold mb-2">{label} <span className="text-zinc-500 text-[11px] font-normal">· {arr?.length || 0} questions</span></p>
                            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                              {arr?.map((q, i) => (
                                <details key={i} className="text-xs">
                                  <summary className="text-zinc-300 cursor-pointer"><span className="font-semibold text-white">Q{i + 1}.</span> {q.q}</summary>
                                  <ul className="mt-1.5 space-y-0.5 ml-4 text-zinc-400">
                                    {q.options?.map((o, k) => (
                                      <li key={k} className={k === q.correct ? "text-[#7EE787]" : ""}>{String.fromCharCode(65 + k)}. {o}{k === q.correct ? "  ✓" : ""}</li>
                                    ))}
                                  </ul>
                                  {q.rationale && <p className="text-zinc-500 italic mt-1">{q.rationale}</p>}
                                </details>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {training.role_play_scenarios?.length > 0 && (
                      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4" data-testid="training-scenarios">
                        <p className="text-white text-sm font-semibold mb-2">Role-play scenarios</p>
                        <div className="space-y-2">
                          {training.role_play_scenarios.map((s, i) => (
                            <div key={i} className="border-l-2 border-[#7EE787]/40 pl-3 py-1">
                              <p className="text-white text-xs font-semibold">{s.title}</p>
                              <p className="text-zinc-400 text-xs mt-0.5">{s.context}</p>
                              {s.participants?.length > 0 && <p className="text-zinc-500 text-[11px] mt-0.5">Participants: {s.participants.join(", ")}</p>}
                              {s.outcome_criteria && <p className="text-emerald-300 text-[11px] mt-0.5">Outcome: {s.outcome_criteria}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {training.trainer_tips?.length > 0 && (
                      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                        <p className="text-amber-300 text-sm font-semibold mb-2">Trainer tips</p>
                        <ul className="space-y-1 text-zinc-300 text-xs list-disc list-inside">
                          {training.trainer_tips.map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeOutput === "automation" && (
              <div className="space-y-4" data-testid="output-automation">
                {automationLoading && <p className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Scanning for automation opportunities…</p>}
                {automationErr && <p className="text-amber-400 text-xs" data-testid="automation-error">{automationErr}</p>}

                {automation && (
                  <>
                    <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800/60 rounded-2xl p-5">
                      <div className="flex flex-wrap items-center gap-5">
                        <div className="text-center">
                          <p className="text-5xl font-bold" style={{
                            color: automation.overall_automation_potential >= 70 ? "#7EE787" : automation.overall_automation_potential >= 40 ? "#FFC857" : "#FF6B6B"
                          }} data-testid="automation-score">{automation.overall_automation_potential}</p>
                          <p className="text-zinc-500 text-[11px] uppercase tracking-wider mt-1">Automation potential</p>
                        </div>
                        <p className="text-zinc-300 text-sm leading-relaxed flex-1 min-w-[240px]">{automation.summary}</p>
                      </div>
                    </div>

                    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden" data-testid="automation-table">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-zinc-950 text-zinc-400">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium">Step → Opportunity</th>
                              <th className="text-left px-3 py-2 font-medium">Tool</th>
                              <th className="text-left px-3 py-2 font-medium">Effort</th>
                              <th className="text-left px-3 py-2 font-medium">Saves</th>
                              <th className="text-left px-3 py-2 font-medium">ROI</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-900">
                            {automation.ideas?.map((idea, i) => {
                              const roiColor = idea.roi_band === "Quick win" ? "text-[#7EE787] border-[#7EE787]/40 bg-[#7EE787]/10" :
                                               idea.roi_band === "Solid" ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" :
                                               idea.roi_band === "Long-term" ? "text-amber-300 border-amber-500/30 bg-amber-500/10" :
                                               "text-zinc-400 border-zinc-700 bg-zinc-800";
                              const effortColor = idea.effort === "Low" ? "text-emerald-300" : idea.effort === "Medium" ? "text-amber-300" : "text-red-300";
                              return (
                                <tr key={i} className="hover:bg-zinc-900/40" data-testid={`automation-row-${idea.id}`}>
                                  <td className="px-3 py-3 align-top">
                                    <p className="text-white font-medium">{idea.step}</p>
                                    <p className="text-zinc-400 mt-0.5">{idea.opportunity}</p>
                                    {idea.implementation_hint && <p className="text-[#7EE787] text-[10px] mt-1 italic">→ {idea.implementation_hint}</p>}
                                  </td>
                                  <td className="px-3 py-3 align-top">
                                    <p className="text-white font-medium">{idea.suggested_tool}</p>
                                    {idea.alternative_tools?.length > 0 && <p className="text-zinc-500 text-[10px] mt-0.5">or: {idea.alternative_tools.join(", ")}</p>}
                                  </td>
                                  <td className={`px-3 py-3 align-top font-semibold ${effortColor}`}>{idea.effort}</td>
                                  <td className="px-3 py-3 align-top text-zinc-300">
                                    <p>{idea.time_saved_per_run_min}m × {idea.runs_per_week}/wk</p>
                                    <p className="text-zinc-500 text-[10px]">= {idea.time_saved_per_run_min * idea.runs_per_week}m/wk</p>
                                  </td>
                                  <td className="px-3 py-3 align-top">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wider ${roiColor}`}>{idea.roi_band}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {automation.quick_wins?.length > 0 && (
                      <div className="bg-[#7EE787]/5 border border-[#7EE787]/20 rounded-xl p-3" data-testid="quick-wins">
                        <p className="text-[#7EE787] text-[11px] uppercase tracking-wider font-bold mb-1">Quick wins ({automation.quick_wins.length})</p>
                        <p className="text-zinc-300 text-xs">Prioritize these for immediate impact: <span className="font-mono text-[#7EE787]">{automation.quick_wins.join(", ")}</span></p>
                      </div>
                    )}

                    {automation.watch_outs?.length > 0 && (
                      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
                        <p className="text-amber-300 text-[11px] uppercase tracking-wider font-bold mb-1 flex items-center gap-1"><AlertTriangle size={11} /> Watch-outs</p>
                        <ul className="text-zinc-300 text-xs space-y-0.5 list-disc list-inside">
                          {automation.watch_outs.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        )}
      </main>

      {/* Upgrade-to-Pro modal */}
      {showUpgrade && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowUpgrade(false)} data-testid="upgrade-modal">
          <div className="bg-zinc-950 border border-[#7EE787]/30 rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Crown size={20} className="text-[#7EE787]" />
                <p className="text-white font-bold text-base">Unlock SOP Studio Pro</p>
              </div>
              <button onClick={() => setShowUpgrade(false)} className="text-zinc-400 hover:text-white p-1" data-testid="close-upgrade"><X size={16} /></button>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Editing saved SOPs, version history with side-by-side diff, and AI-powered Impact Analysis are Pro features.
            </p>
            <ul className="text-zinc-300 text-xs space-y-1.5 list-disc list-inside">
              <li>Full edit-history with up to 20 versions per SOP</li>
              <li>One-click restore to any prior version</li>
              <li>AI Impact Analysis: risk delta, compliance drift, training needs</li>
              <li>Unlimited saves · Priority generation</li>
            </ul>
            <div className="bg-gradient-to-br from-[#7EE787]/10 to-[#43A047]/5 border border-[#7EE787]/30 rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-white text-2xl font-bold">₹999<span className="text-zinc-500 text-sm font-normal">/mo</span></p>
                <p className="text-zinc-500 text-[11px]">or ₹9,990/yr (save 17%)</p>
              </div>
              <Link to="/pricing#sop-studio"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-black font-bold text-xs"
                    style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                    data-testid="upgrade-cta">
                Upgrade <ArrowLeft size={12} className="rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
